import english from "@/i18n/locales/en-US.json";
import french from "@/i18n/locales/fr-CA.json";
import { loadRuntimeConfig } from "@/config/runtime-config-loader";
import { initializeRuntimeConfig } from "@/config/runtime-config";
import type { RuntimeConfig } from "@/config/runtime-config-schema";

interface StartupDependencies {
  load: () => Promise<Readonly<RuntimeConfig>>;
  initialize: (config: Readonly<RuntimeConfig>) => void;
  bootstrap: () => Promise<void>;
}

const defaults: StartupDependencies = {
  load: loadRuntimeConfig,
  initialize: initializeRuntimeConfig,
  bootstrap: async () => {
    const { bootstrap } = await import("@/bootstrap");
    bootstrap();
  },
};

export async function startApplication(
  root: HTMLElement,
  dependencies: StartupDependencies = defaults,
): Promise<void> {
  const language = globalThis.navigator.language.toLowerCase();
  const messages = (language.startsWith("fr") ? french : english).startup;
  root.setAttribute("aria-busy", "true");
  root.textContent = messages.loading;

  let config: Readonly<RuntimeConfig>;
  try {
    config = await dependencies.load();
  } catch {
    console.error(
      "Application startup failed: runtime configuration unavailable",
    );
    root.setAttribute("aria-busy", "false");
    const message = document.createElement("p");
    message.setAttribute("role", "alert");
    message.textContent = messages.configurationError;
    const retry = document.createElement("button");
    retry.type = "button";
    retry.textContent = messages.retry;
    retry.addEventListener(
      "click",
      () => {
        retry.disabled = true;
        void startApplication(root, dependencies);
      },
      { once: true },
    );
    root.replaceChildren(message, retry);
    return;
  }

  try {
    dependencies.initialize(config);
    await dependencies.bootstrap();
    root.removeAttribute("aria-busy");
  } catch {
    console.error(
      "Application startup failed after configuration initialization",
    );
    root.setAttribute("aria-busy", "false");
    root.setAttribute("role", "alert");
    root.textContent = messages.applicationError;
  }
}
